// src/Dashboard/pages/user/DashboardUser/utils/dashboardUserUtils.js

export const DASHBOARD_USER_ACTIVE_TAB_KEY = "mac_dashboard_user_active_tab";

export const DASHBOARD_ROUTES = {
  messaging: "/dashboard/messages",
  payments: "/dashboard/commandes-&-reglements",
  calendar: "/dashboard/calendrier",
};

export const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";

export function toAbsoluteUploadUrl(url, fallback = "") {
  if (!url) return fallback;

  const value = String(url).trim();

  if (
    /^https?:\/\//i.test(value) ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  if (value.startsWith("/uploads")) {
    return `${API_BASE}${value}`;
  }

  return value || fallback;
}

export function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function getResolvedUserId(user) {
  return user?.userId || user?.id || getStoredUserInfo()?.userId || null;
}

export function isReadFlag(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") return value !== "0";
  return !!value;
}

export function formatBadge(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n > 99 ? "99+" : String(n);
}

export function formatDateTimeFR(value) {
  if (!value) return { date: "—", time: "" };

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { date: "—", time: "" };

  return {
    date: date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    }),
    time: date.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    }),
  };
}

export function formatDateLongFR(value) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function formatEUR(value) {
  const n = Number(value || 0);

  return n.toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
  });
}

export function uniqueIds(rows, extractor) {
  return Array.from(
    new Set(
      (Array.isArray(rows) ? rows : [])
        .map(extractor)
        .filter(Boolean)
        .map((id) => Number(id))
    )
  );
}

export function extractTechniqueId(row) {
  return (
    row?.technique_id ||
    row?.Technique?.id ||
    row?.technique?.id ||
    row?.id_technique ||
    null
  );
}

export function extractEventId(row) {
  return row?.event_id || row?.Event?.id || row?.event?.id || row?.id_event || null;
}

export function isPendingReglement(row) {
  const status = String(row?.statut_reglement || "").trim();
  return status && status !== "Réglé";
}

export function getPaymentStatusClass(status) {
  switch (status) {
    case "Non Réglé":
      return "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-200";
    case "En Attente":
      return "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-200";
    case "Partiellement Réglé":
      return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200";
    case "Annulé":
      return "bg-slate-900 text-white";
    case "Remboursé":
      return "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-200";
    default:
      return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200";
  }
}

export function sortByDateDesc(rows, field = "created_at") {
  return [...(rows || [])].sort(
    (a, b) => new Date(b?.[field] || 0) - new Date(a?.[field] || 0)
  );
}

export function sortByDateAsc(rows, field = "date_achat") {
  return [...(rows || [])].sort(
    (a, b) => new Date(a?.[field] || 0) - new Date(b?.[field] || 0)
  );
}

export function getCurrentSeasonLabel(date = new Date()) {
  const year = date.getFullYear();
  const month = date.getMonth();
  const start = month >= 8 ? year : year - 1;
  return `${start}-${start + 1}`;
}

export const NOTIFICATION_CONTENT_FILTER_OPTIONS = [
  {
    value: "",
    label: "Toutes les notifications",
  },
  {
    value: "nouvel utilisateur a été créé",
    label: "Nouvel utilisateur créé",
  },
  {
    value: "Inscription de",
    label: "Inscription",
  },
  {
    value: "a effectué le renouvellement",
    label: "Renouvellement effectué",
  },
  {
    value: "vient d'inscrire",
    label: "Adhérent inscrit par un utilisateur",
  },
  {
    value: "a validé le cours d’essai",
    label: "Cours d’essai validé",
  },
  {
    value: "a effectué un paiement en ligne",
    label: "Paiement en ligne effectué",
  },
  {
    value: "Désinscription de",
    label: "Désinscription",
  },
  {
    value: "a commandé",
    label: "Commande effectuée",
  },
  {
    value: "a confirmé son paiement en ligne",
    label: "Paiement en ligne confirmé",
  },
];

export function normalizeSearchText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[’']/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase()
    .trim();
}

export function getNotificationContent(notification) {
  return (
    notification?.message ||
    notification?.content ||
    notification?.text ||
    notification?.notification ||
    ""
  );
}

export function filterNotificationsByContent(notifications, selectedFilter) {
  const search = normalizeSearchText(selectedFilter);

  if (!search) return notifications || [];

  return (notifications || []).filter((notification) => {
    const content = normalizeSearchText(getNotificationContent(notification));
    return content.includes(search);
  });
}